/**
 * Copy invite link (Warren, 29 October 2026): "Copy invite link" next to
 * "Re-send invite" for every invite type. It makes a fresh single-use link
 * within the invite's expiry, shows it once to copy, and audits who copied
 * it; "Email from my account" opens the operator's mail app to the invitee
 * with the link and a short note filled in. Why: a Sales Partner's invite
 * email landed in his Outlook quarantine, so the operator sends the link from
 * their own mail.
 *
 * Addendum, same day: "Copy invite link" goes in each row's Actions next to
 * Edit, Re-send invite and Deactivate (and on the partner page next to
 * Re-send invite), for every invite type. "Any admin who can re-send an
 * invite can copy its link."
 *
 * The Api is the authority: each type's link endpoint runs that type's resend
 * gate, so the Admin shows Copy invite link exactly where and when it shows
 * Re-send invite. The visibility rules below are the ones each screen uses
 * for BOTH controls, so the two cannot drift apart.
 *
 * This file holds the words (English; the Sales Staff page passes its own
 * locale's, lib/salesTeamCopy.ts), the mailto note and those rules. The
 * dialog is components/ui/InviteLinkDialog.tsx.
 */

/**
 * What every link endpoint answers, once. For an invite to an account that already exists (Warren, 29 October 2026:
 * "accepting it asks them to sign in"), a team or sales staff link is the sign-in page: existing_account, no expiry,
 * nothing replaced.
 */
export interface InviteLink { url: string; expires_at: string | null; existing_account?: boolean }

export interface InviteLinkCopy {
  /** The action, beside Re-send invite. */
  action: string;
  title: string;
  field: string;
  loading: string;
  copy: string;
  copied: string;
  /** The select-all fallback when the clipboard refuses. */
  selected: string;
  email: string;
  /** Holds {date}. */
  replaces: string;
  discard: string;
  expired: string;
  failed: string;
  close: string;
  mailSubject: string;
  /** Holds {name}. */
  mailGreeting: string;
  mailGreetingNoName: string;
  /** Holds {date}. */
  mailBody: string;
  mailSignoff: string;
  /** The line under a sign-in page link (an existing account), instead of the replaces line. */
  signInPage: string;
  /** The note's body for a sign-in page link (no date). */
  mailSignInPage: string;
}

export const INVITE_LINK_COPY_EN: InviteLinkCopy = {
  action: 'Copy invite link',
  title: 'Copy invite link',
  field: 'Invite link',
  loading: 'Making a fresh link...',
  copy: 'Copy',
  copied: 'Copied',
  selected: 'The link is selected. Press Ctrl+C, or Cmd+C on a Mac, to copy it.',
  email: 'Email from my account',
  replaces: 'This link replaces the one in earlier invite emails. It works once and expires {date}.',
  discard: 'Closing this window discards the link. It is never shown again.',
  expired: 'This invite has expired. Re-send it to start a new one, then copy that link.',
  failed: 'The invite link could not be made.',
  close: 'Close',
  mailSubject: 'Your EV Initiative invite',
  mailGreeting: 'Hi {name},',
  mailGreetingNoName: 'Hello,',
  mailBody: 'I am sending your EV Initiative invite from my own email in case the earlier one did not reach you. The link below works once and expires {date}.',
  mailSignoff: 'Thanks,',
  signInPage: 'They already have an EV Initiative account with this email, so this link opens the sign-in page. Signing in with that account accepts the invite.',
  mailSignInPage: 'I am sending your EV Initiative invite from my own email in case the earlier one did not reach you. You already have an EV Initiative account with this email, so sign in with it at the link below to accept.',
};

/** The Api's refusal code for an invite past its expiry. */
export const INVITE_EXPIRED_CODE = 'INVITE_EXPIRED';

/** Fills {name} placeholders once; an unknown name stays as written. */
export function fillInviteCopy(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (Object.hasOwn(values, name) ? values[name]! : match));
}

const DASHES = new RegExp(`[${String.fromCharCode(0x2013)}${String.fromCharCode(0x2014)}]`, 'g');

/** The expiry as a date in the viewer's language ("November 19, 2026"); never a dash, in any locale. */
export function inviteExpiryDate(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  let text: string;
  try { text = d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { text = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  return text.replace(DASHES, '-');
}

/** "Sam" from "Sam Rivera"; null when no name was given. */
export function inviteFirstName(name?: string | null): string | null {
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first ? first : null;
}

/** The address part of a mailto: percent-encoded, with the @ kept readable (RFC 6068 allows either). */
function mailtoAddress(address: string): string {
  return encodeURIComponent(address.trim()).replace(/%40/gi, '@');
}

/** The note "Email from my account" fills in: the operator's own voice, the link and its expiry. Lines end CRLF (RFC 6068). */
export function inviteLinkNote(o: {
  url: string; expiresAt: string | null; inviteeName?: string | null; senderName?: string | null; copy?: InviteLinkCopy; locale?: string;
}): string {
  const c = o.copy ?? INVITE_LINK_COPY_EN;
  const first = inviteFirstName(o.inviteeName);
  const sender = (o.senderName ?? '').trim();
  return [
    first ? fillInviteCopy(c.mailGreeting, { name: first }) : c.mailGreetingNoName,
    '',
    o.expiresAt ? fillInviteCopy(c.mailBody, { date: inviteExpiryDate(o.expiresAt, o.locale) }) : c.mailSignInPage,
    '',
    o.url,
    '',
    c.mailSignoff,
    ...(sender ? [sender] : []),
  ].join('\r\n');
}

/**
 * "Email from my account": a mailto: link to the invitee with the subject and
 * the note, every part percent-encoded (the link's own ? & = included), so the
 * operator's mail app opens with it filled in and the url intact.
 */
export function inviteLinkMailto(o: {
  to: string; url: string; expiresAt: string | null; inviteeName?: string | null; senderName?: string | null; copy?: InviteLinkCopy; locale?: string;
}): string {
  const c = o.copy ?? INVITE_LINK_COPY_EN;
  return `mailto:${mailtoAddress(o.to)}?subject=${encodeURIComponent(c.mailSubject)}&body=${encodeURIComponent(inviteLinkNote(o))}`;
}

/** The inline error: the expired copy for INVITE_EXPIRED, else the Api's reason, else the fallback. */
export function inviteLinkErrorText(code: string | null, message: string | null, copy: InviteLinkCopy = INVITE_LINK_COPY_EN): string {
  if (code === INVITE_EXPIRED_CODE) return copy.expired;
  const reason = (message ?? '').trim();
  return reason || copy.failed;
}

// ── Where Re-send invite and Copy invite link show (the same rule each) ─────

/** A Sales Partner whose invite is still open: active and not accepted. The
 *  row adds can_edit, the profile capabilities.resend_invite, the edit form
 *  disables both buttons while inactive. */
export function partnerInviteOpen(a: { active: boolean; invite_accepted_at?: string | null }): boolean {
  return a.active && !a.invite_accepted_at;
}

/** A teammate's row: what the Api says THIS viewer may do (can_resend_invite: the resend's gate, the teammate never
 *  above the viewer's tier, never signed in, active; review finding B), on an invite still outstanding. Never the
 *  persona: a row without the flag shows neither action. */
export function teamInviteOpen(u: { state: string; last_login?: string | null; can_resend_invite?: boolean }): boolean {
  return u.can_resend_invite === true && (u.state === 'pending' || u.state === 'expired') && !u.last_login;
}

/** A sales staff invite still waiting (the list is the Super Admin's only). */
export function salesStaffInviteOpen(i: { status: string }): boolean {
  return i.status === 'pending';
}

/** A building staff invite pending or expired (the card shows only to whoever the Api lets invite there). */
export function buildingStaffInviteOpen(inv: { status: string }): boolean {
  return inv.status !== 'accepted';
}
